import React, { useEffect, useRef, useState } from "react";
import {
  ComponentConfig,
  DropZone,
  WithId,
  WithPuckProps,
} from "@measured/puck";

export interface GridProps {
  options?: {
    desktop?: {
      numColumns?: string;
      numRows?: string;
      gap?: number;
      placeContent?: "center" | "end" | "start" | "normal";
    };
    tablet?: {
      numColumns?: string;
      numRows?: string;
      gap?: number;
      placeContent?: "center" | "end" | "start" | "normal";
    };
    mobile?: {
      numColumns?: string;
      numRows?: string;
      gap?: number;
      placeContent?: "center" | "end" | "start" | "normal";
    };
  };
}

const parseValues = (value?: string): string[] =>
  value?.split(",").map((v) => v.trim()) ?? [];

const Grid = (props: WithId<WithPuckProps<GridProps>>) => {
  const { options, id } = props;
  const [viewport, setViewport] = useState<"mobile" | "tablet" | "desktop">(
    "desktop",
  );
  const uniqueClass = `grid-responsive-${id}`;

  useEffect(() => {
    const width = props.editMode
      ? (document.getElementById("preview-frame")?.clientWidth ?? 1024)
      : window.innerWidth;

    if (width < 744) setViewport("mobile");
    else if (width < 1024) setViewport("tablet");
    else setViewport("desktop");
  }, []);

  const defaultConfig = options?.[viewport] ?? { numColumns: "", numRows: "" };
  const allColumns = parseValues(defaultConfig?.numColumns);
  const allRows = parseValues(defaultConfig?.numRows);

  const css = `
      @media only screen and (max-width: 743px) {
          .${uniqueClass} {
            display: grid;
            grid-template-columns: ${parseValues(options?.mobile?.numColumns)
              .map((v) => `${v}fr`)
              .join(" ")};
            grid-template-rows: ${parseValues(options?.mobile?.numRows)
              .map((v) => `${v}fr`)
              .join(" ")};
            gap: ${options?.mobile?.gap ?? 24}px;
          }
        }

        @media only screen and (min-width: 744px) and (max-width: 1023px) {
          .${uniqueClass} {
            display: grid;
            grid-template-columns: ${parseValues(options?.tablet?.numColumns)
              .map((v) => `${v}fr`)
              .join(" ")};
            grid-template-rows: ${parseValues(options?.tablet?.numRows)
              .map((v) => `${v}fr`)
              .join(" ")};
            gap: ${options?.tablet?.gap ?? 24}px;
          }
        }

        @media only screen and (min-width: 1024px) {
          .${uniqueClass} {
            display: grid;
            grid-template-columns: ${parseValues(options?.desktop?.numColumns)
              .map((v) => `${v}fr`)
              .join(" ")};
            grid-template-rows: ${parseValues(options?.desktop?.numRows)
              .map((v) => `${v}fr`)
              .join(" ")};
            gap: ${options?.desktop?.gap ?? 24}px;
          }
        }
  `;

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: css }} />

      <div className={uniqueClass}>
        {allRows.map((_, rowIdx) =>
          allColumns.map((_, colIdx) => (
            <DropZone
              key={`${rowIdx}-${colIdx}`}
              zone={`row-${rowIdx}-col-${colIdx}`}
              style={{ placeContent: options[viewport].placeContent }}
            />
          )),
        )}
      </div>
    </>
  );
};

export const PuckGrid: ComponentConfig<GridProps> = {
  label: "Grid",
  fields: {
    options: {
      type: "object",
      label: "Options",
      objectFields: {
        desktop: {
          type: "object",
          label: "Desktop",
          objectFields: {
            numColumns: { type: "text", label: "Number of Columns" },
            numRows: { type: "text", label: "Number of Rows" },
            gap: { type: "number", label: "Gap", min: 0 },
            placeContent: {
              type: "select",
              label: "Place Content",
              options: [
                { label: "Normal", value: "normal" },
                { label: "Center", value: "center" },
                { label: "End", value: "end" },
                { label: "Start", value: "start" },
              ],
            },
          },
        },
        tablet: {
          type: "object",
          label: "Tablet",
          objectFields: {
            numColumns: { type: "text", label: "Number of Columns" },
            numRows: { type: "text", label: "Number of Rows" },
            gap: { type: "number", label: "Gap", min: 0 },
            placeContent: {
              type: "select",
              label: "Place Content",
              options: [
                { label: "Normal", value: "normal" },
                { label: "Center", value: "center" },
                { label: "End", value: "end" },
                { label: "Start", value: "start" },
              ],
            },
          },
        },
        mobile: {
          type: "object",
          label: "Mobile",
          objectFields: {
            numColumns: { type: "text", label: "Number of Columns" },
            numRows: { type: "text", label: "Number of Rows" },
            gap: { type: "number", label: "Gap", min: 0 },
            placeContent: {
              type: "select",
              label: "Place Content",
              options: [
                { label: "Normal", value: "normal" },
                { label: "Center", value: "center" },
                { label: "End", value: "end" },
                { label: "Start", value: "start" },
              ],
            },
          },
        },
      },
    },
  },
  defaultProps: {
    options: {
      desktop: { numColumns: "1,1", numRows: "1,1", gap: 16 },
      tablet: { numColumns: "1,1", numRows: "1,1", gap: 16 },
      mobile: { numColumns: "1,1", numRows: "1,1", gap: 16 },
    },
  },
  render: (props) => <Grid {...props} />,
};
